import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/utils/api";

export function StatCard({ label, value, accent = "slate", sub, testid }) {
  const accentColor = {
    sky: "text-sky-400",
    emerald: "text-emerald-400",
    amber: "text-amber-300",
    rose: "text-rose-300",
    slate: "text-slate-100",
  }[accent] || "text-slate-100";
  return (
    <div className="rounded-lg bg-[#0E1A3A] border border-slate-800 p-4" data-testid={testid}>
      <p className="text-xs text-slate-500 uppercase tracking-wide">{label}</p>
      <p className={`text-3xl font-semibold mt-1 ${accentColor}`}>{value}</p>
      {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
    </div>
  );
}

export function Section({ title, description, children, right, testid }) {
  return (
    <section className="mb-10" data-testid={testid}>
      <div className="mb-4 flex items-end justify-between gap-4">
        <div>
          {title && <h2 className="text-xl font-semibold text-slate-100">{title}</h2>}
          {description && <p className="text-sm text-slate-400 mt-1">{description}</p>}
        </div>
        {right}
      </div>
      {children}
    </section>
  );
}

export function PageHeader({ user, subtitle }) {
  const first = user.full_name?.split(" ")[0] || "there";
  return (
    <section className="mb-10 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
      <div>
        <h1 className="text-3xl font-semibold text-slate-100" data-testid="dashboard-welcome">
          Welcome, {first}
        </h1>
        {subtitle && <p className="mt-2 text-slate-400 max-w-2xl">{subtitle}</p>}
      </div>
      <div className="md:w-72 flex-shrink-0">
        <CreditsCard role={user.role} />
      </div>
    </section>
  );
}

export function ToolTiles() {
  return (
    <section className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
      <a
        href="/tools/bone-age"
        data-testid="dashboard-tile-bone-age"
        className="group p-6 rounded-xl bg-gradient-to-br from-sky-500/10 to-indigo-500/10 border border-slate-700 hover:border-sky-400 transition"
      >
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-lg font-semibold text-slate-100">Bone Age Prediction</h3>
            <p className="text-sm text-slate-400 mt-1 max-w-xs">
              AI bone age from a single hand X-ray.
            </p>
          </div>
          <span className="text-2xl" aria-hidden>🦴</span>
        </div>
        <span className="mt-4 inline-block text-sm text-sky-400 group-hover:text-sky-300">Open tool →</span>
      </a>
      <a
        href="/tools/growth"
        data-testid="dashboard-tile-growth"
        className="group p-6 rounded-xl bg-gradient-to-br from-amber-500/10 to-orange-500/10 border border-slate-700 hover:border-amber-400 transition"
      >
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-lg font-semibold text-slate-100">Growth Assessment</h3>
            <p className="text-sm text-slate-400 mt-1 max-w-xs">
              Predicted adult height, GHI & IAP-2015 percentile.
            </p>
          </div>
          <span className="text-2xl" aria-hidden>📏</span>
        </div>
        <span className="mt-4 inline-block text-sm text-amber-300 group-hover:text-amber-200">Open tool →</span>
      </a>
      <a
        href="/tools/nin-nutriguide"
        data-testid="dashboard-tile-nutriguide"
        className="group p-6 rounded-xl bg-gradient-to-br from-emerald-500/10 to-teal-500/10 border border-slate-700 hover:border-emerald-400 transition"
      >
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-lg font-semibold text-slate-100">NIN-NutriGuide</h3>
            <p className="text-sm text-slate-400 mt-1 max-w-xs">
              Personalized nutrition analysis with ICMR-NIN RDA data.
            </p>
          </div>
          <span className="text-2xl" aria-hidden>🥗</span>
        </div>
        <span className="mt-4 inline-block text-sm text-emerald-400 group-hover:text-emerald-300">Open tool →</span>
      </a>
    </section>
  );
}


/** Credits balance widget — pulls /api/payments/me. Shows for paying roles only. */
export function CreditsCard({ role }) {
  const [balance, setBalance] = useState(null);
  const [toolCredits, setToolCredits] = useState({});
  const isUnlimited = role === "super_admin" || role === "admin";
  const isPaying = ["parent", "doctor", "school_admin", "class_teacher"].includes(role);

  useEffect(() => {
    if (!isPaying) return;
    api.get("/api/payments/me").then(({ data }) => { setBalance(data.balance ?? 0); setToolCredits(data.tool_credits || {}); }).catch(() => setBalance(0));
  }, [isPaying]);

  if (isUnlimited) {
    return (
      <div className="rounded-xl bg-gradient-to-br from-violet-500/10 to-fuchsia-500/10 border border-violet-500/30 p-5"
        data-testid="credits-card">
        <p className="text-xs uppercase tracking-wider text-violet-300">Report credits</p>
        <p className="text-3xl font-bold text-white mt-1">Unlimited</p>
        <p className="text-xs text-white/50 mt-1">Admin role — no per-report charges</p>
      </div>
    );
  }
  if (!isPaying) return null;

  const low = balance !== null && balance <= 2;
  return (
    <div className={`rounded-xl border p-5 transition ${low ? "bg-rose-500/[0.06] border-rose-500/30" : "bg-emerald-500/[0.06] border-emerald-500/30"}`}
      data-testid="credits-card">
      <p className="text-xs uppercase tracking-wider text-emerald-300">Report credits</p>
      <div className="flex items-baseline gap-2 mt-1">
        <p className={`text-3xl font-bold ${low ? "text-rose-300" : "text-white"}`} data-testid="credit-balance">
          {balance === null ? "…" : balance}
        </p>
        <p className="text-xs text-white/50">remaining</p>
      </div>
      {(toolCredits.growth > 0 || toolCredits.nutrition > 0) && (
        <p className="text-xs text-amber-200 mt-1" data-testid="tool-credits-line">
          + Parent Quiz credits: {toolCredits.growth || 0} Growth · {toolCredits.nutrition || 0} NutriGuide
        </p>
      )}
      <Link to="/checkout"
        className="mt-3 inline-block text-xs text-emerald-400 hover:text-emerald-300 underline"
        data-testid="dashboard-buy-credits-link">
        Buy more credits →
      </Link>
    </div>
  );
}
